import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Users, Truck, MapPin, Plus, X, RefreshCw, Clock, CheckCircle2, UserPlus, Link2 } from "lucide-react";
import { toast } from "sonner";

// Pair Selector Component for creating Technician + Helper pairs
function PairSelector({ availableEmployees, onAddPair, onAddDriver }) {
  const [selectedTech, setSelectedTech] = useState(null);
  const [selectedHelper, setSelectedHelper] = useState(null);
  const [adding, setAdding] = useState(false);

  const handleAddPair = async () => {
    if (!selectedTech || !selectedHelper) {
      toast.error("Please select both a Technician and a Helper");
      return;
    }
    setAdding(true);
    try {
      await onAddPair(selectedTech, selectedHelper);
      setSelectedTech(null);
      setSelectedHelper(null);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Pair Selection */}
      <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Link2 size={16} className="text-orange-500" />
          <span className="font-bold text-sm text-slate-700">Create Working Pair</span>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {/* Technician Selection */}
          <div>
            <label className="block text-xs font-bold text-green-600 uppercase mb-2">Technician</label>
            <select
              value={selectedTech?.id || ""}
              onChange={(e) => {
                const tech = availableEmployees.technicians?.find(t => t.id === e.target.value);
                setSelectedTech(tech || null);
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              data-testid="select-technician"
            >
              <option value="">Select Technician</option>
              {availableEmployees.technicians?.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.full_name}</option>
              ))}
            </select>
            {selectedTech && (
              <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-700 font-medium">
                ✓ {selectedTech.full_name}
              </div>
            )}
          </div>

          {/* Helper Selection */}
          <div>
            <label className="block text-xs font-bold text-purple-600 uppercase mb-2">Helper</label>
            <select
              value={selectedHelper?.id || ""}
              onChange={(e) => {
                const helper = availableEmployees.helpers?.find(h => h.id === e.target.value);
                setSelectedHelper(helper || null);
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="select-helper"
            >
              <option value="">Select Helper</option>
              {availableEmployees.helpers?.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.full_name}</option>
              ))}
            </select>
            {selectedHelper && (
              <div className="mt-2 p-2 bg-purple-50 rounded text-xs text-purple-700 font-medium">
                ✓ {selectedHelper.full_name}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handleAddPair}
          disabled={!selectedTech || !selectedHelper || adding}
          className="w-full mt-4 px-4 py-2.5 bg-gradient-to-r from-green-500 to-purple-500 text-white rounded font-bold text-sm hover:from-green-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="add-pair-btn"
        >
          {adding ? (
            <>Adding Pair...</>
          ) : (
            <>
              <UserPlus size={16} />
              Add Pair to Task Force
            </>
          )}
        </button>
      </div>

      {/* Driver Selection (Separate) */}
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Truck size={16} className="text-blue-500" />
          <span className="font-bold text-sm text-slate-700">Add Driver (Optional)</span>
        </div>
        <div className="space-y-2">
          {availableEmployees.drivers?.length === 0 ? (
            <p className="text-slate-400 text-sm">No available drivers</p>
          ) : availableEmployees.drivers?.map(emp => (
            <div key={emp.id} className="flex items-center justify-between p-2 border border-slate-100 rounded hover:bg-blue-50">
              <span className="text-sm">{emp.full_name}</span>
              <button 
                onClick={() => onAddDriver(emp)} 
                className="px-3 py-1 bg-blue-500 text-white rounded text-xs font-bold hover:bg-blue-600"
              >
                Add Driver
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DailyRosterPage() {
  const { user } = useAuth();
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState({ technicians: [], helpers: [], drivers: [] });
  const [feeders, setFeeders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  
  const [form, setForm] = useState({
    roster_date: selectedDate,
    feeder_code: "",
    vehicle_id: "",
    driver_id: "",
    members: [],
    notes: ""
  });

  const fetchMyRoster = async () => {
    setLoading(true);
    try {
      const res = await api.get("/rosters/my-roster", { params: { roster_date: selectedDate } });
      setRoster(res.data.roster);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const [feedersRes, vehiclesRes, availRes] = await Promise.all([
        api.get("/hierarchy/feeders", { params: { limit: 200 } }),
        api.get("/vehicles", { params: { status: "active", limit: 50 } }),
        api.get("/rosters/available-employees", { params: { roster_date: selectedDate } })
      ]);
      setFeeders(feedersRes.data);
      setVehicles(vehiclesRes.data?.items || []);
      setAvailableEmployees(availRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { 
    fetchMyRoster(); 
    fetchResources();
  }, [selectedDate]);

  const handleCreateRoster = async (e) => {
    e.preventDefault();
    if (!form.feeder_code) {
      toast.error("Please select a feeder");
      return;
    }
    
    setCreating(true);
    try {
      const res = await api.post("/rosters", { ...form, roster_date: selectedDate });
      setRoster(res.data);
      setShowCreate(false);
      toast.success("Task Force created!");
      fetchResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create roster");
    } finally {
      setCreating(false);
    }
  };

  const handleAddMember = async (employee, role) => {
    if (!roster) return;
    try {
      await api.post(`/rosters/${roster.id}/members`, { employee_id: employee.id, role });
      toast.success(`Added ${employee.full_name}`);
      fetchMyRoster();
      fetchResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add member");
    }
  };

  const handleRemoveMember = async (employeeId) => {
    if (!roster) return;
    try {
      await api.delete(`/rosters/${roster.id}/members/${employeeId}`);
      toast.success("Member removed");
      fetchMyRoster();
      fetchResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleCloseRoster = async () => {
    if (!roster || !window.confirm("Close this roster and generate day summary?")) return;
    try {
      const res = await api.post(`/rosters/${roster.id}/close`);
      toast.success(`Roster closed. MI: ${res.data.summary?.mi || 0}`);
      fetchMyRoster();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close roster");
    }
  };

  const isSupervisor = user?.role === "supervisor";
  const canEdit = isSupervisor || user?.role === "admin" || user?.role === "ci";

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="daily-roster-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            Daily Task Force
          </h1>
          <p className="text-slate-500 text-sm">Create and manage your team for today's field work</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
            <Calendar size={16} className="text-orange-500" />
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="border-0 text-sm font-semibold text-slate-700 focus:outline-none"
              data-testid="date-picker"
            />
          </div>
          <button onClick={fetchMyRoster} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
          <p className="text-slate-500">Loading roster...</p>
        </div>
      ) : roster ? (
        /* Existing Roster View */
        <div className="space-y-4">
          {/* Roster Header Card */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-sm p-5 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${roster.status === "active" ? "bg-green-500" : "bg-slate-500"}`}>
                    {roster.status}
                  </span>
                  <span className="text-orange-200 text-xs">{roster.roster_date}</span>
                </div>
                <h2 className="text-xl font-bold mb-1">{roster.feeder_info?.feeder_name || roster.feeder_code}</h2>
                <p className="text-orange-100 text-sm">{roster.feeder_info?.circle_name} • {roster.feeder_info?.division_name}</p>
              </div>
              {roster.active_shutdown && (
                <div className="bg-green-500 px-3 py-2 rounded-sm text-xs font-bold">
                  <CheckCircle2 size={14} className="inline mr-1" />
                  Shutdown Active
                </div>
              )}
            </div>
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <Users size={16} />
                <span>{roster.members?.length || 0} Members</span>
              </div>
              {roster.vehicle_info && (
                <div className="flex items-center gap-2">
                  <Truck size={16} />
                  <span>{roster.vehicle_info.vehicle_number}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{roster.today_installations || 0} Installations Today</span>
              </div>
            </div>
          </div>

          {/* Team Members - Grouped by Pairs */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800">Team Members</h3>
              {canEdit && roster.status === "active" && (
                <button onClick={() => setShowAddMember(true)} className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded-sm text-xs font-bold hover:bg-orange-600" data-testid="add-member-btn">
                  <UserPlus size={14} />Add Pair
                </button>
              )}
            </div>
            <div className="p-4">
              {roster.members?.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Users size={32} className="mx-auto mb-2" />
                  <p>No team members yet</p>
                  <p className="text-xs">Add technician + helper pairs to your task force</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Display Pairs */}
                  {(() => {
                    const technicians = roster.members?.filter(m => m.role === "technician") || [];
                    const helpers = roster.members?.filter(m => m.role === "helper") || [];
                    const drivers = roster.members?.filter(m => m.role === "driver") || [];
                    const pairs = technicians.map((tech, i) => ({ tech, helper: helpers[i] }));
                    
                    return (
                      <>
                        {/* Pairs */}
                        {pairs.map((pair, i) => (
                          <div key={i} className="border border-slate-200 rounded-lg p-3 bg-gradient-to-r from-green-50 to-purple-50">
                            <div className="flex items-center gap-2 mb-2">
                              <Link2 size={12} className="text-orange-500" />
                              <span className="text-xs font-bold text-slate-500 uppercase">Pair {i + 1}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              {/* Technician */}
                              <div className="flex-1 flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xs">
                                  {pair.tech?.full_name?.charAt(0) || "T"}
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-800 text-sm">{pair.tech?.full_name}</div>
                                  <div className="text-xs text-green-600 uppercase font-bold">Technician</div>
                                </div>
                                {canEdit && roster.status === "active" && (
                                  <button onClick={() => handleRemoveMember(pair.tech?.employee_id)} className="ml-auto p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                              
                              {/* Connector */}
                              <div className="text-slate-300">+</div>
                              
                              {/* Helper */}
                              {pair.helper ? (
                                <div className="flex-1 flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-xs">
                                    {pair.helper?.full_name?.charAt(0) || "H"}
                                  </div>
                                  <div>
                                    <div className="font-semibold text-slate-800 text-sm">{pair.helper?.full_name}</div>
                                    <div className="text-xs text-purple-600 uppercase font-bold">Helper</div>
                                  </div>
                                  {canEdit && roster.status === "active" && (
                                    <button onClick={() => handleRemoveMember(pair.helper?.employee_id)} className="ml-auto p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                      <X size={14} />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="flex-1 text-center text-slate-400 text-sm italic">No helper assigned</div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {/* Unpaired Helpers */}
                        {helpers.length > technicians.length && (
                          <div className="border border-dashed border-purple-200 rounded-lg p-3 bg-purple-50">
                            <div className="text-xs font-bold text-purple-500 uppercase mb-2">Unpaired Helpers</div>
                            {helpers.slice(technicians.length).map((helper, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-xs">
                                  {helper.full_name?.charAt(0)}
                                </div>
                                <span className="text-sm">{helper.full_name}</span>
                                {canEdit && roster.status === "active" && (
                                  <button onClick={() => handleRemoveMember(helper.employee_id)} className="ml-auto p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Drivers */}
                        {drivers.length > 0 && (
                          <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
                            <div className="text-xs font-bold text-blue-500 uppercase mb-2">Driver(s)</div>
                            {drivers.map((driver, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xs">
                                  {driver.full_name?.charAt(0)}
                                </div>
                                <span className="text-sm font-medium">{driver.full_name}</span>
                                {canEdit && roster.status === "active" && (
                                  <button onClick={() => handleRemoveMember(driver.employee_id)} className="ml-auto p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          {canEdit && roster.status === "active" && (
            <div className="flex justify-end">
              <button onClick={handleCloseRoster} className="px-4 py-2 bg-slate-800 text-white rounded-sm text-sm font-bold hover:bg-slate-900" data-testid="close-roster-btn">
                Close Roster & Generate Summary
              </button>
            </div>
          )}

          {/* Day Summary (if closed) */}
          {roster.status === "closed" && roster.day_summary && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <h3 className="font-bold text-slate-800 mb-3">Day Summary</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div><div className="text-2xl font-black text-blue-600">{roster.day_summary.surveys}</div><div className="text-xs text-slate-500">Surveys</div></div>
                <div><div className="text-2xl font-black text-purple-600">{roster.day_summary.dtm}</div><div className="text-xs text-slate-500">DTM</div></div>
                <div><div className="text-2xl font-black text-indigo-600">{roster.day_summary.dtc}</div><div className="text-xs text-slate-500">DTC</div></div>
                <div><div className="text-2xl font-black text-green-600">{roster.day_summary.mi}</div><div className="text-xs text-slate-500">MI</div></div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* No Roster - Create New */
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-orange-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">No Task Force for {selectedDate}</h2>
          <p className="text-slate-500 mb-6">Create a daily roster to start field operations</p>
          {canEdit ? (
            <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-sm font-bold hover:bg-orange-600" data-testid="create-roster-btn">
              <Plus size={18} />Create Task Force
            </button>
          ) : (
            <p className="text-slate-400 text-sm">Contact your supervisor to be added to a roster</p>
          )}
        </div>
      )}

      {/* Create Roster Modal */}
      {showCreate && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-sm p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Create Task Force</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <form onSubmit={handleCreateRoster} className="space-y-4">
              <div>
                <label className="field-label">Date</label>
                <input type="date" className="field-input" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} data-testid="roster-date-input" />
              </div>
              <div>
                <label className="field-label">Feeder *</label>
                <select className="field-select" required value={form.feeder_code} onChange={e => setForm({ ...form, feeder_code: e.target.value })} data-testid="feeder-select">
                  <option value="">Select feeder to work on</option>
                  {feeders.map(f => (
                    <option key={f.feeder_code} value={f.feeder_code}>{f.feeder_code} - {f.feeder_name} ({f.circle_name})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Vehicle</label>
                <select className="field-select" value={form.vehicle_id} onChange={e => setForm({ ...form, vehicle_id: e.target.value })}>
                  <option value="">Select vehicle (optional)</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.vehicle_number} - {v.vehicle_type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">Notes</label>
                <textarea className="field-input" rows={2} placeholder="Any special instructions..." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60" data-testid="submit-roster-btn">
                  {creating ? "Creating..." : "Create Task Force"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Member Modal - Pair-based */}
      {showAddMember && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-sm p-6 w-full max-w-lg shadow-xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Add Team Pair</h3>
              <button onClick={() => setShowAddMember(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-4">
              <p className="text-sm text-orange-700">
                <strong>💡 Pair Mode:</strong> Select a Technician and Helper to form a working pair. You can also add a Driver separately.
              </p>
            </div>

            <PairSelector
              availableEmployees={availableEmployees}
              onAddPair={async (tech, helper) => {
                if (!roster) return;
                try {
                  // Add technician
                  await api.post(`/rosters/${roster.id}/members`, { employee_id: tech.id, role: "technician" });
                  // Add helper
                  await api.post(`/rosters/${roster.id}/members`, { employee_id: helper.id, role: "helper" });
                  toast.success(`Pair added: ${tech.full_name} + ${helper.full_name}`);
                  fetchMyRoster();
                  fetchResources();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to add pair");
                }
              }}
              onAddDriver={async (driver) => {
                if (!roster) return;
                try {
                  await api.post(`/rosters/${roster.id}/members`, { employee_id: driver.id, role: "driver" });
                  toast.success(`Driver added: ${driver.full_name}`);
                  fetchMyRoster();
                  fetchResources();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to add driver");
                }
              }}
            />

            <button onClick={() => setShowAddMember(false)} className="w-full mt-4 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600">Done</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
